import React, { useEffect, useState } from 'react';
import { databaseService } from '../../services/database.service';
import { i18nService } from '../../services/i18n.service';
import AdminLayout from '../../components/layout/AdminLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LanguageTabs from '../../components/common/LanguageTabs';
import toast from 'react-hot-toast';

const TermsManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentLang, setCurrentLang] = useState('nl');
  const [content, setContent] = useState({ nl: '', fr: '', de: '', en: '' });

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const data = await databaseService.getPageContent('termsAndConditions');
      if (data) {
        setContent(i18nService.extractMultiLangField(data, 'content'));
      }
    } catch (error) {
      console.error('Error fetching terms:', error);
      toast.error('Failed to load terms and conditions');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ...i18nService.setTranslatedFields('content', content),
        lastUpdated: Date.now()
      };
      await databaseService.update('website/pages/termsAndConditions', dataToSave);
      toast.success('Terms and conditions saved successfully');
    } catch (error) {
      toast.error('Failed to save terms and conditions');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Terms & Conditions Manager</h1>
          <p className="text-gray-600">Edit your terms and conditions content</p>
        </div>

        <Card>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Terms & Conditions Content
            </label>
            <LanguageTabs
              currentLang={currentLang}
              onLangChange={setCurrentLang}
            />
            <textarea
              value={content[currentLang]}
              onChange={(e) => setContent({ ...content, [currentLang]: e.target.value })}
              className="w-full min-h-[500px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              placeholder={`Enter your terms and conditions content here in ${currentLang.toUpperCase()}. You can use HTML tags for formatting...`}
            />
            <p className="text-sm text-gray-500 mt-2">
              You can use HTML tags for formatting (e.g., &lt;h2&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt;).
              Switch between language tabs to edit content in different languages.
            </p>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Suggested Sections:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Introduction and Acceptance</li>
              <li>• Use of Website</li>
              <li>• Intellectual Property Rights</li>
              <li>• Limitation of Liability</li>
              <li>• Governing Law (Belgian)</li>
              <li>• Contact Information</li>
            </ul>
          </div>
        </Card>

        <div className="flex justify-end">
          <Button variant="primary" onClick={handleSave} loading={saving} disabled={saving}>
            {saving ? 'Saving...' : 'Save Terms & Conditions'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default TermsManager;
